import { Dropdown } from '@acko-tech/building-blocks.ui.dropdown';
import { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { IOrder } from '../types';
import { OrderStatus } from '../transactions/constants';
import { getDD_MM_YYYY, getDD_Month_Time } from 'utils/date';
import TransactionItemCardV2 from './transactionItemCardV2';
import { ITransactionProps, trasactionFilterTypes } from './types';
import * as Styles from './styles';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import { ModalDesktop, ModalMobile } from '@acko-tech/building-blocks.ui.modal';
import TransactionDetailsModalV2 from './transactionDetailModalV2';
import { getPlanName, whatsCoverText, getImageBasePath } from 'utils/generics';
import SnackBar from 'library/snackbar';
import { useRouter } from 'next/router';
import { MPosContext } from 'utils/context';
import {
    SegmentEventNameTypes,
    SegmentEventButtonNames,
    SegmentEventActionTypes,
    SegmentEventFieldTypes,
    SegmentEventActionNames,
} from 'utils/segment/constants';

// segment
import sendSegmentEvent from 'utils/segment';
import { cancelOrderApi } from 'components/dashboard/transactionsV2/transactionDetailModalV2/api';
import { useFetchParams } from 'hooks/useFetchParams';
import SearchInput from '@acko-tech/building-blocks.ui.search-input';

const TransactionsV2: React.FC<ITransactionProps> = (props) => {
    const {
        orders,
        hasMore,
        onLoadMore,
        refresh,
        searchValue,
        onSearchChange,
        clearSearch,
    } = props;
    const { partnerId, userDetails } = useContext(MPosContext);
    const { lob } = useFetchParams();
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const router = useRouter();

    const { isMobile } = useResponsiveDevice();
    const [modalStatus, setModalStatus] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
    const [isBtnLoading, setIsBtnLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });

    const [type, setType] = useState({
        displayValue: 'All',
        value: 'all',
    });
    const [triggered, setTriggered] = useState(false);

    const filterTypeChangeHandler = ({ value, displayValue }) => {
        setType((prevState) => ({
            ...prevState,
            value,
            displayValue,
        }));

        // segment Events
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_name: `select_${displayValue}_transacions`,
                button_name: SegmentEventButtonNames.SELECT_TRANSACTIONS_TYPE,
                action_type: SegmentEventActionTypes.TAP_BTN,
                field_selected: SegmentEventFieldTypes.DROPDOWN,
            },
            {
                userDetails,
                isMobile,
            }
        );
    };

    useEffect(() => {
        const dropdownElements =
            document.getElementsByClassName('dropwdownWidth');
        if (dropdownElements && dropdownElements.length > 0) {
            const dropdownElement: any = dropdownElements[0];
            dropdownElement.style.minWidth = '150px';
        }
    }, []);

    useEffect(() => {
        if (triggered) {
            onLoadMore(type.value);
        } else {
            setTriggered(true);
        }
    }, [type]);

    const orderStatus = (status: string, statusMessage: string) => {
        switch (status) {
            case OrderStatus.ORDER_CREATED:
            case OrderStatus.PAYMENT_INITIATED_BY_DEALER:
            case OrderStatus.PAYMENT_INITIATED_BY_CUSTOMER:
                return {
                    text: 'PENDING',
                    color: '#5920C5',
                    background: '#EFE9FB',
                };
            case OrderStatus.PAYMENT_SUCCESS_BY_DEALER:
            case OrderStatus.PAYMENT_SUCCESS_BY_CUSTOMER:
                return {
                    text: 'POLICY ISSUANCE PENDING',
                    color: '#5920C5',
                    background: '#EFE9FB',
                };

            case OrderStatus.POLICY_CREATED:
                return {
                    text: 'COMPLETED',
                    background: '#99fc9d',
                    color: '#0c843f',
                };
            default:
                return {
                    text: statusMessage.toUpperCase(),
                    color: '#D83D37',
                    background: '#FBEAEA',
                };
        }
    };

    const getValueObjects = (order: IOrder) => {
        const { text, color, background } = orderStatus(
            order.status,
            order.status_message
        );

        const value2 =
            order.status === OrderStatus.POLICY_CREATED
                ? order?.policy_issued_on
                    ? getDD_Month_Time(new Date(order.policy_issued_on))
                    : getDD_Month_Time(new Date(order.updated_on))
                : getDD_Month_Time(new Date(order.updated_on));

        return {
            value1: order.device.name,
            value2,
            value3: order.customer.phone,
            value4: text,
            labelColor: {
                color,
                background,
            },
        };

        // if (type.value === TabOptions.PENDING) {
        //     const { text, color, background } = orderStatus(
        //         order.status,
        //         order.status_message
        //     );
        //     return {
        //         value1: order.device.name,
        //         value2: getDD_Month_Time(new Date(order.updated_on)),
        //         value3: order.customer.phone,
        //         value4: text,
        //         labelColor: {
        //             color,
        //             background,
        //         },
        //     };
        // }
        return {
            value1: order.customer.name,
            value2: `₹${order.plan.premium}`,
            value3: order.device.name,
            value4: order.device.purchase_date,
        };
    };

    const onClickOrder = (order: IOrder) => {
        setModalStatus(true);
        setSelectedOrder(order);

        // segment Events
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_name: SegmentEventActionNames.CLICK_TRANSACTION_ITEM,
                action_type: SegmentEventActionTypes.TAP_BTN,
            },
            {
                userDetails,
                isMobile,
            }
        );
    };

    const onHideTransactionDetailsModal = () => {
        setModalStatus(false);
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_name:
                    SegmentEventActionNames.CLOSE_TRANSACTION_DETAILS_MODAL,
                action_type: SegmentEventActionTypes.TAP_BTN,
            },
            { userDetails, isMobile }
        );
    };

    const onClick = async (action: string) => {
        if (selectedOrder) {
            if (action === 'pay') {
                if (selectedOrder.payment_details?.wallet_enabled) {
                    router.push(
                        `/${lob}/${partnerId}/payment-mode?order_ekey=${selectedOrder?.ekey}`
                    );
                } else {
                    setIsBtnLoading(true);
                    import('./apis').then(async (req) => {
                        const { data, status, statusCode } =
                            await req.dealerPayment(
                                partnerId,
                                selectedOrder?.ekey
                            );
                        setIsBtnLoading(false);
                        if (statusCode === 401) {
                            router.push('/login');
                            setSnackbar({
                                type: 'failure',
                                message:
                                    'Token expired. Redirecting to login page.',
                            });
                            return;
                        }
                        if (status && data) {
                            if (status && data?.payment_gateway_url) {
                                window.location.href = `${data.payment_gateway_url}?id=${data?.payment_order_ekey}&redirect_source=pos_dealer`;
                            }
                            return;
                        }
                        setSnackbar({
                            type: 'failure',
                            message: 'Oops. Something went wrong.',
                        });
                    });
                }
            } else if (action === 'resend') {
                import('./apis').then(async (req) => {
                    setIsBtnLoading(true);
                    const { status, statusCode, errorMessage } =
                        await req.resendLinkToCustomer(
                            partnerId,
                            selectedOrder?.ekey || ''
                        );
                    if (statusCode === 401) {
                        router.push(`/login`);
                        setSnackbar({
                            type: 'failure',
                            message:
                                'Token expired. Redirecting to login page.',
                        });
                        return;
                    }
                    setIsBtnLoading(false);
                    if (status) {
                        setSnackbar({
                            type: 'success',
                            message: 'Payment link has sent successfully.',
                        });
                        return;
                    }
                    setSnackbar({
                        type: 'failure',
                        message: errorMessage || 'Oops. Something went wrong.',
                    });
                });
            } else if (action === 'cancel') {
                try {
                    const isSent = await cancelOrderApi(
                        partnerId,
                        selectedOrder.ekey
                    );
                    if (isSent) {
                        setSnackbar({
                            message: 'Order Cancelled Successfully',
                            type: 'success',
                        });
                    }
                    setModalStatus(false);
                    refresh();
                } catch (e) {
                    setSnackbar({
                        message: 'Failed to cancel order',
                        type: 'failure',
                    });
                } finally {
                }
            }
        } else {
            setSnackbar({
                type: 'failure',
                message: 'No order selected.',
            });
        }
    };

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;
    // ModalMobile doesn't read these props, but keep them desktop-only so
    // the mobile bottom sheet stays intentionally full-width and isn't
    // coupled to a desktop-only layout constant.
    const desktopModalWidthProps = isMobile
        ? {}
        : { maxWidth: '560px', minimumWidth: '560px' };

    const searchPlaceholder =
        lob === 'auto-lob'
            ? 'Search by customer phone or vin'
            : 'Search by customer phone or serial number';

    const toggleSearch = () => {
        if (isSearchOpen) {
            if (searchValue) {
                clearSearch();
            }
            setIsSearchOpen(false);
        } else {
            setIsSearchOpen(true);
        }
    };

    return (
        <Styles.Wrapper>
            <Styles.TransactionBox>
                <Styles.TrasactionList>Transactions</Styles.TrasactionList>
                <Styles.TransactionHeaderActions>
                    <Styles.SearchIcon
                        src={
                            isSearchOpen
                                ? getImageBasePath('/icons/cross.svg')
                                : getImageBasePath('/icons/search.svg')
                        }
                        alt={isSearchOpen ? 'close' : 'search'}
                        onClick={toggleSearch}
                    />
                    <Styles.TransactionDropDown $isHidden={isSearchOpen}>
                        <Dropdown
                            className="dropwdownWidth"
                            label={'filter'}
                            value={type}
                            options={trasactionFilterTypes}
                            onChange={filterTypeChangeHandler}
                        />
                    </Styles.TransactionDropDown>
                </Styles.TransactionHeaderActions>
            </Styles.TransactionBox>
            {isSearchOpen && (
                <Styles.SearchInputWrapper>
                    <SearchInput
                        className=""
                        placeholder={searchPlaceholder}
                        handleChange={(value) => onSearchChange(value)}
                    />
                </Styles.SearchInputWrapper>
            )}
            <div>
                {orders.length === 0 ? (
                    <Styles.NoResult>No Transactions</Styles.NoResult>
                ) : (
                    <InfiniteScroll
                        dataLength={orders.length}
                        next={() => onLoadMore(type.value)}
                        hasMore={hasMore}
                        loader={<></>}
                    >
                        {orders.map((order, index) => (
                            <Styles.ItemCardWrapper
                                key={order.ekey}
                                onClick={() => onClickOrder(order)}
                            >
                                <TransactionItemCardV2
                                    {...getValueObjects(order)}
                                />
                                {index !== orders.length - 1 && <Styles.Hr />}
                            </Styles.ItemCardWrapper>
                        ))}
                    </InfiniteScroll>
                )}
            </div>
            <Modal
                header={<></>}
                show={modalStatus}
                onHide={onHideTransactionDetailsModal}
                padding={'20px'}
                // ModalDesktop outer shell defaults to max-width: fit-content and
                // collapses around flex text; `minimumWidth` sets that outer
                // max-width. Desktop-only — see desktopModalWidthProps above.
                {...desktopModalWidthProps}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                showOverlayBackground={true}
            >
                {selectedOrder && (
                    <TransactionDetailsModalV2
                        deviceName={selectedOrder?.device?.name}
                        invoice_url={selectedOrder?.plan.invoice_url}
                        lastUpdatedAt={
                            selectedOrder.status === OrderStatus.POLICY_CREATED
                                ? selectedOrder?.policy_issued_on
                                    ? getDD_MM_YYYY(
                                          new Date(
                                              selectedOrder.policy_issued_on
                                          )
                                      )
                                    : getDD_MM_YYYY(
                                          new Date(selectedOrder.updated_on)
                                      )
                                : getDD_MM_YYYY(
                                      new Date(selectedOrder.updated_on)
                                  )
                        }
                        status={selectedOrder?.status}
                        statusMessage={selectedOrder?.status_message}
                        serialNumber={selectedOrder?.device?.serial_number}
                        planName={getPlanName(selectedOrder?.plan.plan_name)}
                        mobileNumber={selectedOrder.customer.phone}
                        amount={selectedOrder.plan.settle_with_acko}
                        deviceCategory={selectedOrder.device.category}
                        coverage={whatsCoverText(
                            selectedOrder.plan.plan_category?.toLowerCase()
                        )}
                        onClick={onClick}
                        isBtnLoading={isBtnLoading}
                        vin={selectedOrder?.device?.vin}
                        policyNumber={
                            selectedOrder?.policy_details?.policy_number
                        }
                        policyEKey={selectedOrder?.policy_details?.contract_id}
                        paymentPaidBy={
                            selectedOrder?.payment_details?.user_type
                        }
                        orderEKey={selectedOrder?.ekey}
                    />
                )}
            </Modal>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.Wrapper>
    );
};

export default TransactionsV2;
